import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coins, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CreditsPage() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [creditPacks, setCreditPacks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }

    axios.get(`${API_URL}/api/credit-packs`).then(res => setCreditPacks(res.data));
    axios.get(`${API_URL}/api/credits`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setTransactions(res.data.recent_transactions || []));
  }, [token, navigate]);

  const handlePurchase = async (packId) => {
    setLoading(packId);
    try {
      const res = await axios.post(`${API_URL}/api/credits/purchase`, {
        pack_id: packId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen hero-gradient pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4"><span className="gradient-text">{t('credits_title')}</span></h1>
          <p className="text-slate-400">{t('credits_subtitle')}</p>
        </div>

        {user && (
          <div className="glass rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center">
                  <Coins className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">{t('credits_available')}</p>
                  <p className="text-4xl font-bold text-white">{user.credits}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-slate-400 text-sm">{t('credits_current_plan')}</p>
                <p className="text-xl font-semibold text-emerald-400 capitalize">{user.plan}</p>
                <p className="text-slate-500 text-sm">{t('max')} {user.max_llms} LLMs</p>
              </div>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-bold text-white mb-6">{t('credits_buy')}</h2>
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {creditPacks.map((pack) => (
            <motion.div key={pack.id} whileHover={{ scale: 1.02 }} className="glass rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white mb-1">{pack.credits}</p>
              <p className="text-slate-400 text-sm mb-4">{t('credits')}</p>
              <button
                onClick={() => handlePurchase(pack.id)}
                disabled={loading === pack.id}
                className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading === pack.id && <Loader2 className="w-4 h-4 animate-spin" />}
                ${pack.price}
              </button>
            </motion.div>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-white mb-6">{t('credits_history')}</h2>
        <div className="glass rounded-xl overflow-hidden">
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-slate-400">{t('credits_no_tx')}</div>
          ) : (
            <div className="divide-y divide-white/5">
              {transactions.map((tx, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white">{tx.reason}</p>
                    <p className="text-slate-400 text-sm">{new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`font-semibold ${tx.amount > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
